export interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: {
    name: string
    avatar?: string
  }
  category: string
  publishedAt: string
  readTime: string
  featured?: boolean
  image?: string
}

export const blogPosts: BlogPost[] = [
  {
    id: "1",
    slug: "why-scheduled-gaming-works",
    title: "Why Scheduled Gaming Actually Works for Busy Adults",
    excerpt: "The science behind why treating game nights like appointments leads to more actual gaming time.",
    content: `
      <p>If you're reading this, you probably know the frustration. The group chat lights up with "we should play tonight," and somehow it never happens. Work runs late, kids need attention, or you're just too tired to coordinate logistics at 9 PM.</p>
      
      <p>Here's what we've learned from running leagues for over two years: the magic isn't in finding more time—it's in protecting the time you already have.</p>
      
      <h2>The Commitment Effect</h2>
      <p>Psychologists call it the "commitment and consistency principle." When we make a commitment—especially one that involves other people and a small financial investment—we're dramatically more likely to follow through. It's why gym memberships work better than home workouts for most people, and why rec sports leagues have thrived for decades.</p>
      
      <h2>Your Calendar Is Your Friend</h2>
      <p>When Tuesday at 9 PM is "Valorant night" on your calendar, it stops competing with everything else. Your partner knows you're unavailable. Your brain stops treating it as optional. It becomes part of your routine, not an interruption to it.</p>
      
      <h2>The Numbers Don't Lie</h2>
      <p>Our internal data shows that league players game 3-4x more consistently than they did before joining. And 94% of registered players show up to their scheduled matches. That's not because our players are more dedicated—it's because the structure makes showing up the default.</p>
    `,
    author: { name: "Alex Chen" },
    category: "Community",
    publishedAt: "May 12, 2026",
    readTime: "4 min read",
    featured: true
  },
  {
    id: "2",
    slug: "summer-2026-season-preview",
    title: "Summer 2026 Season Preview: New Games and Regions",
    excerpt: "Everything you need to know about our biggest season yet, including three new games and expanded coverage.",
    content: `
      <p>Summer is almost here, and we're excited to announce our biggest season yet. Here's what's coming.</p>
      
      <h2>New Games</h2>
      <p>We're adding three highly-requested titles to our lineup: Marvel Rivals, Counter-Strike 2, and Helldivers 2. Each will launch with leagues in at least four regions.</p>
      
      <h2>Expanded Regions</h2>
      <p>Based on community feedback, we're splitting the West region into two: Pacific (CA, NV, AZ) and Mountain (CO, UT, NM). This means better timezone alignment and more local connections.</p>
      
      <h2>Registration Opens June 1</h2>
      <p>Mark your calendars. Annual members get priority access starting May 25.</p>
    `,
    author: { name: "Sarah Martinez" },
    category: "Announcements",
    publishedAt: "May 8, 2026",
    readTime: "3 min read"
  },
  {
    id: "3",
    slug: "making-friends-as-adult-gamer",
    title: "Making Friends as an Adult Gamer (It's Harder Than It Should Be)",
    excerpt: "Why adult friendships are difficult and how shared activities—like gaming leagues—can help.",
    content: `
      <p>Making friends as an adult is genuinely hard. Research shows that after age 25, most people's social circles start shrinking. We get busy, we move, we lose touch. And making new friends? That requires repeated, unplanned interactions—something adult life doesn't naturally provide.</p>
      
      <h2>The Third Place Problem</h2>
      <p>Sociologists talk about "third places"—spaces that aren't home or work where community happens. For our parents, it might have been church, the local bar, or the bowling league. For many of us, those spaces don't exist anymore.</p>
      
      <h2>Gaming as a Third Place</h2>
      <p>Online gaming has the potential to be a third place, but random matchmaking doesn't cut it. You need repeated interactions with the same people. You need shared goals. You need a reason to show up consistently.</p>
      
      <p>That's what leagues provide. The same teammates, every week, working toward something together. It's not magic—it's just creating the conditions where friendships can actually form.</p>
    `,
    author: { name: "Alex Chen" },
    category: "Community",
    publishedAt: "May 1, 2026",
    readTime: "5 min read"
  },
  {
    id: "4",
    slug: "rocket-league-beginner-tips",
    title: "Rocket League Tips for Players Returning After Years Away",
    excerpt: "The meta has changed. Here's what you need to know to get back in the game.",
    content: `
      <p>So you played Rocket League back in 2018 and want to jump back in? Good news: the core game is the same. Bad news: everyone got way better. Here's how to catch up.</p>
      
      <h2>Fundamentals First</h2>
      <p>Before you try to learn ceiling shots, make sure your ground game is solid. Consistent aerials, fast recoveries, and good positioning will win more games than flashy mechanics.</p>
      
      <h2>Free Play Is Your Friend</h2>
      <p>Spend 15-20 minutes in free play before your sessions. Just hit the ball around. Get comfortable with your car again. This alone will accelerate your improvement dramatically.</p>
      
      <h2>Rotation Matters More Than Ever</h2>
      <p>At almost every rank, the team that rotates better wins. If you're not sure where to be, default to "behind your teammates." Space is good.</p>
    `,
    author: { name: "Mike Thompson" },
    category: "Game Guides",
    publishedAt: "April 25, 2026",
    readTime: "6 min read"
  },
  {
    id: "5",
    slug: "spring-2026-playoffs-recap",
    title: "Spring 2026 Playoffs Recap: Upsets and Underdog Stories",
    excerpt: "A look back at an incredible playoff run, including the Valorant team that went from last place to champions.",
    content: `
      <p>Spring 2026 playoffs wrapped last weekend, and what a season it was. Here are the highlights.</p>
      
      <h2>The Cinderella Story</h2>
      <p>Phoenix Rising entered playoffs as the 8th seed in Northeast Valorant, having lost their first four matches of the season. They finished the regular season 6-4 and then ran the table in playoffs, beating the 1-seed in the finals.</p>
      
      <h2>Record-Breaking Attendance</h2>
      <p>We had our highest-ever attendance rate this season: 94.2% across all leagues. That's nearly 2,400 players showing up week after week.</p>
      
      <h2>What's Next</h2>
      <p>Summer registration opens June 1. See you there.</p>
    `,
    author: { name: "Sarah Martinez" },
    category: "Recaps",
    publishedAt: "April 18, 2026",
    readTime: "4 min read"
  }
]

export const categories = ["All", "Community", "Announcements", "Game Guides", "Recaps"]
